import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  PenTool, Sparkles, FileCheck, Target, TrendingUp, Shield, Bell, 
  CheckCircle, Loader2, ArrowRight, Zap, Clock, Users
} from "lucide-react";
import { LOGO_URL, API } from "../common/constants";

const ScribeFlowStudioPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        product: "scribe_flow_studio",
        name: name.trim()
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join waitlist");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AiQ Phoenix Tech" className="h-10 w-auto" />
          </Link>
          <Badge variant="outline" className="border-purple-500/50 text-purple-400">
            <Clock className="w-3 h-3 mr-1" /> Coming Soon
          </Badge>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-background to-background" />
        <div className="relative container-main py-16">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="w-20 h-20 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-6">
              <PenTool className="w-10 h-10 text-purple-400" />
            </div>
            
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
              <Zap className="w-3 h-3 mr-1" /> AI Writing Ecosystem
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-violet-400 text-transparent bg-clip-text">
                Scribe Flow Studio
              </span>
            </h1>
            
            <p className="text-xl text-muted max-w-2xl mx-auto">
              Your AI-powered writing ecosystem. From document generation to content optimization, 
              create professional content in any format with intelligent assistance.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-surface">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Card className="border-purple-500/20 bg-purple-500/5">
              <CardContent className="pt-6 text-center">
                <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">AI-Powered Writing</h3>
                <p className="text-sm text-muted">Intelligent suggestions and auto-completion</p>
              </CardContent>
            </Card>
            <Card className="border-purple-500/20 bg-purple-500/5">
              <CardContent className="pt-6 text-center">
                <FileCheck className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Document Generation</h3>
                <p className="text-sm text-muted">Create polished, structured professional documents</p>
              </CardContent>
            </Card>
            <Card className="border-purple-500/20 bg-purple-500/5">
              <CardContent className="pt-6 text-center">
                <Target className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Content Optimization</h3>
                <p className="text-sm text-muted">SEO and readability improvements</p>
              </CardContent>
            </Card>
            <Card className="border-purple-500/20 bg-purple-500/5">
              <CardContent className="pt-6 text-center">
                <TrendingUp className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Multi-Format Export</h3>
                <p className="text-sm text-muted">PDF, DOCX, HTML, Markdown and more</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Waitlist Form */}
      <section className="py-16">
        <div className="container-main">
          <Card className="max-w-lg mx-auto border-purple-500/30">
            <CardContent className="pt-8 pb-8">
              {success ? (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold">You're on the list!</h3>
                  <p className="text-muted">
                    We'll notify you at <span className="text-foreground">{email}</span> when Scribe Flow Studio launches.
                  </p>
                  <Link to="/">
                    <Button variant="outline" className="mt-4">
                      <ArrowRight className="w-4 h-4 mr-2" /> Back to Home
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <Bell className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                    <h2 className="text-xl font-heading font-bold mb-2">Get Early Access</h2>
                    <p className="text-sm text-muted">
                      Be the first to know when Scribe Flow Studio launches. Join the waitlist today.
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="scribe-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name">Name (Optional)</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="scribe-name-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-purple-500 hover:bg-purple-600"
                      disabled={loading}
                      data-testid="scribe-submit-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining...</>
                      ) : (
                        <><Bell className="w-4 h-4 mr-2" /> Join the Waitlist</>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Badge */}
      <section className="py-12 bg-surface">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
            <Shield className="w-12 h-12 text-purple-400" />
            <div>
              <h3 className="font-semibold mb-1">Built by AiQ Phoenix Tech</h3>
              <p className="text-sm text-muted">
                Source-grounded AI designed to enhance your writing while minimizing unsupported content.
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted">
              <Users className="w-4 h-4" />
              <span>Join other content creators on the waitlist</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="container-main text-center">
          <p className="text-sm text-muted">
            © 2026 AiQ Phoenix Tech. Where bold ideas ignite powerful, dependable tech. | <Link to="/" className="text-purple-400 hover:underline">Back to Home</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ScribeFlowStudioPage;
